import { useApiClient } from '@yuksalish/api-client'
import type {
  AllocationHistoryRow,
  CostingFilters,
  CostingSnapshot,
  CounterpartyBalance,
  CounterpartyBalanceFilters,
  CurrencyOption,
  DateRangeFilters,
  FinanceAdjustment,
  FinanceAdjustmentFilters,
  FinanceAdjustmentPayload,
  FinanceFlag,
  FinanceId,
  FinancialAccount,
  FinancialAccountFilters,
  FinancialAccountPayload,
  FinancialTransfer,
  FinancialTransferPayload,
  Invoice,
  InvoiceFilters,
  Option,
  PagedResult,
  PageMeta,
  PayablesAgingFilters,
  PayablesAgingRow,
  PayablesAgingSummary,
  Payment,
  PaymentFilters,
  PaymentPayload,
  LaborAccrual,
  LaborAccrualFilters,
  PayrollAdjustment,
  PayrollAdjustmentFilters,
  PayrollAdjustmentPayload,
  PayrollPeriod,
  PayrollPeriodFilters,
  PayrollPeriodPayload,
  PayrollPosting,
  PayrollPostingFilters,
  PayrollAccrualReconciliationRow,
  PayrollAccrualReconciliationSummary,
  PayrollEmployeeStatement,
  PayrollPostingReconciliationRow,
  PayrollPostingReconciliationSummary,
  PayrollReportFilters,
  PayrollSummaryReport,
  PayrollRunDetail,
  PayrollRunFilters,
  PayrollRunPayload,
  PayrollRunSummary,
  PricelistDetail,
  PricelistOption,
  ProductOption,
  PurchaseOrderFilters,
  PurchaseOrderOption,
  ReportResult,
  RevenueVsCollectionsRow,
  RevenueVsCollectionsTotals,
  CashFlowSummaryRow,
  CashFlowSummaryTotals,
  CurrencyExposureRow,
  CurrencyExposureTotals,
  EmployeeOption,
  FinanceReconciliationRow,
  FinanceReconciliationTotals,
  SupplierInvoice,
  SupplierInvoiceFilters,
  SupplierInvoicePayload,
  TimesheetReportFilters,
  TimesheetReportRow,
  TreasuryTransaction,
} from '../types/finance'

interface ApiList<T> {
  data: T[]
  meta?: {
    page?: number
    current_page?: number
    per_page?: number
    total?: number
    total_pages?: number
    last_page?: number
  }
}

interface ApiItem<T> {
  data: T
}

interface ApiReport<TRow, TSummary> {
  data: TRow[]
  meta?: ApiList<never>['meta']
  summary?: TSummary | null
}

const MAX_PAGES = 8

function parseMeta(
  meta: ApiList<never>['meta'],
  fallbackPage = 1,
  fallbackPerPage = 20,
): PageMeta {
  return {
    page: meta?.page ?? meta?.current_page ?? fallbackPage,
    per_page: meta?.per_page ?? fallbackPerPage,
    total: meta?.total ?? 0,
    total_pages: meta?.total_pages ?? meta?.last_page ?? 1,
  }
}

/** Some finance endpoints answer bare arrays — normalise them into a page shape. */
function asPaged<T>(items: T[], meta?: ApiList<never>['meta']): PagedResult<T> {
  if (meta && typeof meta.total === 'number') {
    return { items, meta: parseMeta(meta, 1, items.length || 20) }
  }
  return {
    items,
    meta: { page: 1, per_page: items.length || 20, total: items.length, total_pages: 1 },
  }
}

function asReport<TRow, TSummary>(
  payload: ApiReport<TRow, TSummary>,
): ReportResult<TRow, TSummary> {
  return {
    rows: payload.data ?? [],
    meta: parseMeta(payload.meta, 1, (payload.data ?? []).length || 20),
    summary: payload.summary ?? null,
  }
}

function upper(value?: string): string | undefined {
  const trimmed = value?.trim()
  return trimmed ? trimmed.toUpperCase() : undefined
}

/** Query params shared by every `/reports/payroll/*` endpoint. */
function reportParams(filters: PayrollReportFilters) {
  return {
    payroll_period_id: idOrUndefined(filters.payroll_period_id),
    employee_id: idOrUndefined(filters.employee_id),
    currency_code: upper(filters.currency_code),
    date_from: filters.date_from || undefined,
    date_to: filters.date_to || undefined,
  }
}

function idOrUndefined(value?: FinanceId | '' | null): FinanceId | undefined {
  return value === '' || value === null || value === undefined ? undefined : value
}

export function useFinanceApi() {
  const client = useApiClient()

  // --- Reference data ----------------------------------------------------------

  /** Active currencies (`GET /currencies`) for every money select in the app. */
  async function listCurrencies(): Promise<CurrencyOption[]> {
    const response = await client.get<ApiList<CurrencyOption>>('/currencies', {
      params: { is_active: 1, page: 1, per_page: 100 },
    })
    return response.data.data ?? []
  }

  /** Every agent of one role (`GET /agents?role=…`), walking the result pages. */
  async function listAllAgentsByRole(role: string): Promise<Option[]> {
    const collected: Option[] = []
    let page = 1
    let totalPages = 1

    while (page <= totalPages && page <= MAX_PAGES) {
      const response = await client.get<ApiList<Option>>('/agents', {
        params: { role, is_active: 1, page, per_page: 100 },
      })
      collected.push(...(response.data.data ?? []))
      totalPages = response.data.meta?.total_pages ?? response.data.meta?.last_page ?? 1
      page += 1
    }

    return collected
  }

  /** Customers and suppliers, merged and de-duplicated. */
  async function listCounterpartyOptions(): Promise<Option[]> {
    const [customers, suppliers] = await Promise.all([
      listAllAgentsByRole('customer'),
      listAllAgentsByRole('supplier'),
    ])
    const merged = new Map<string, Option>()
    for (const agent of [...customers, ...suppliers]) {
      if (!merged.has(String(agent.id))) merged.set(String(agent.id), agent)
    }
    return [...merged.values()].sort((a, b) => a.name.localeCompare(b.name))
  }

  /** Departments and warehouses, used by the supplier-invoice allocation form. */
  async function listWorkplaceOptions(): Promise<Option[]> {
    const roles = ['department', 'internal', 'warehouse']
    const results = await Promise.all(roles.map((role) => listAllAgentsByRole(role)))
    const merged = new Map<string, Option>()
    for (const agent of results.flat()) {
      if (!merged.has(String(agent.id))) merged.set(String(agent.id), agent)
    }
    return [...merged.values()].sort((a, b) => a.name.localeCompare(b.name))
  }

  // --- Counterparty balances ---------------------------------------------------

  /** Receivables / payables per counterparty (`GET /agents/counterparty-balances`). */
  async function listCounterpartyBalances(
    filters: CounterpartyBalanceFilters = {},
  ): Promise<CounterpartyBalance[]> {
    const response = await client.get<ApiList<CounterpartyBalance>>(
      '/agents/counterparty-balances',
      {
        params: {
          agent_type: filters.agent_type,
          currency_code: upper(filters.currency_code),
          search: filters.search || undefined,
        },
      },
    )
    return (response.data.data ?? []).map((row) => ({
      ...row,
      balances: row.balances ?? [],
    }))
  }

  // --- Sales invoices (receivables) -------------------------------------------

  async function listInvoices(filters: InvoiceFilters = {}): Promise<Invoice[]> {
    const response = await client.get<ApiList<Invoice>>('/invoices', {
      params: {
        agent_id: idOrUndefined(filters.agent_id),
        status: filters.status || undefined,
        currency_code: upper(filters.currency_code),
        order_id: idOrUndefined(filters.order_id),
        shipment_id: idOrUndefined(filters.shipment_id),
      },
    })
    return response.data.data ?? []
  }

  async function getInvoice(id: FinanceId): Promise<Invoice> {
    const response = await client.get<ApiItem<Invoice>>(`/invoices/${id}`)
    return response.data.data
  }

  // --- Payments ----------------------------------------------------------------

  async function listPayments(filters: PaymentFilters = {}): Promise<Payment[]> {
    const response = await client.get<ApiList<Payment>>('/payments', {
      params: {
        agent_id: idOrUndefined(filters.agent_id),
        payment_method: filters.payment_method || undefined,
        direction: filters.direction || undefined,
        currency_code: upper(filters.currency_code),
      },
    })
    return response.data.data ?? []
  }

  async function getPayment(id: FinanceId): Promise<Payment> {
    const response = await client.get<ApiItem<Payment>>(`/payments/${id}`)
    return response.data.data
  }

  async function createPayment(payload: PaymentPayload): Promise<Payment> {
    const response = await client.post<ApiItem<Payment>>('/payments', payload)
    return response.data.data
  }

  async function updatePayment(id: FinanceId, payload: PaymentPayload): Promise<Payment> {
    const response = await client.put<ApiItem<Payment>>(`/payments/${id}`, payload)
    return response.data.data
  }

  async function deletePayment(id: FinanceId): Promise<void> {
    await client.delete(`/payments/${id}`)
  }

  async function reversePayment(id: FinanceId): Promise<void> {
    await client.post(`/payments/${id}/reverse`, {})
  }

  /** Invoice allocation ledger (`GET /finance/payment-allocation-history`). */
  async function listAllocationHistory(
    type: 'customer' | 'supplier',
    invoiceId: FinanceId,
  ): Promise<AllocationHistoryRow[]> {
    const response = await client.get<ApiList<AllocationHistoryRow>>(
      '/finance/payment-allocation-history',
      { params: { type, invoice_id: invoiceId } },
    )
    return response.data.data ?? []
  }

  // --- Supplier invoices -------------------------------------------------------

  async function listSupplierInvoices(
    filters: SupplierInvoiceFilters = {},
  ): Promise<SupplierInvoice[]> {
    const response = await client.get<ApiList<SupplierInvoice>>('/supplier-invoices', {
      params: {
        supplier_agent_id: idOrUndefined(filters.supplier_agent_id),
        currency_code: upper(filters.currency_code),
        status: filters.status || undefined,
      },
    })
    return response.data.data ?? []
  }

  async function getSupplierInvoice(id: FinanceId): Promise<SupplierInvoice> {
    const response = await client.get<ApiItem<SupplierInvoice>>(`/supplier-invoices/${id}`)
    return response.data.data
  }

  async function createSupplierInvoice(payload: SupplierInvoicePayload): Promise<SupplierInvoice> {
    const response = await client.post<ApiItem<SupplierInvoice>>('/supplier-invoices', payload)
    return response.data.data
  }

  async function updateSupplierInvoice(
    id: FinanceId,
    payload: Partial<SupplierInvoicePayload> & { status?: 'void' },
  ): Promise<SupplierInvoice> {
    const response = await client.put<ApiItem<SupplierInvoice>>(`/supplier-invoices/${id}`, payload)
    return response.data.data
  }

  async function deleteSupplierInvoice(id: FinanceId): Promise<void> {
    await client.delete(`/supplier-invoices/${id}`)
  }

  /** Settled statuses are allocation-derived: only `void` may be set directly. */
  async function voidSupplierInvoice(id: FinanceId): Promise<SupplierInvoice> {
    const response = await client.put<ApiItem<SupplierInvoice>>(`/supplier-invoices/${id}`, {
      status: 'void',
    })
    return response.data.data
  }

  /**
   * Purchase orders that can be linked to a supplier invoice. Requires
   * `purchase_orders.view`, so failures are surfaced to the caller.
   */
  async function listPurchaseOrders(
    filters: PurchaseOrderFilters = {},
  ): Promise<PurchaseOrderOption[]> {
    const response = await client.get<ApiList<PurchaseOrderOption>>('/purchase-orders', {
      params: {
        supplier_agent_id: idOrUndefined(filters.supplier_agent_id),
        currency_code: upper(filters.currency_code),
        status: filters.status || undefined,
        page: 1,
        per_page: 100,
      },
    })
    return response.data.data ?? []
  }

  // --- Financial accounts ------------------------------------------------------

  async function listFinancialAccounts(
    filters: FinancialAccountFilters = {},
  ): Promise<FinancialAccount[]> {
    const response = await client.get<ApiList<FinancialAccount>>('/financial-accounts', {
      params: {
        currency_code: upper(filters.currency_code),
        is_active: filters.is_active === '' ? undefined : filters.is_active,
      },
    })
    return response.data.data ?? []
  }

  async function getFinancialAccount(id: FinanceId): Promise<FinancialAccount> {
    const response = await client.get<ApiItem<FinancialAccount>>(`/financial-accounts/${id}`)
    return response.data.data
  }

  async function createFinancialAccount(
    payload: FinancialAccountPayload,
  ): Promise<FinancialAccount> {
    const response = await client.post<ApiItem<FinancialAccount>>('/financial-accounts', payload)
    return response.data.data
  }

  async function updateFinancialAccount(
    id: FinanceId,
    payload: Partial<FinancialAccountPayload>,
  ): Promise<FinancialAccount> {
    const response = await client.put<ApiItem<FinancialAccount>>(`/financial-accounts/${id}`, payload)
    return response.data.data
  }

  async function listAccountTransactions(accountId: FinanceId): Promise<TreasuryTransaction[]> {
    const response = await client.get<ApiList<TreasuryTransaction>>(
      `/financial-accounts/${accountId}/transactions`,
    )
    return response.data.data ?? []
  }

  // --- Financial transfers -----------------------------------------------------

  async function listFinancialTransfers(): Promise<FinancialTransfer[]> {
    const response = await client.get<ApiList<FinancialTransfer>>('/financial-transfers')
    return response.data.data ?? []
  }

  async function getFinancialTransfer(id: FinanceId): Promise<FinancialTransfer> {
    const response = await client.get<ApiItem<FinancialTransfer>>(`/financial-transfers/${id}`)
    return response.data.data
  }

  async function createFinancialTransfer(
    payload: FinancialTransferPayload,
  ): Promise<FinancialTransfer> {
    const response = await client.post<ApiItem<FinancialTransfer>>('/financial-transfers', payload)
    return response.data.data
  }

  async function reverseFinancialTransfer(id: FinanceId): Promise<void> {
    await client.post(`/financial-transfers/${id}/reverse`, {})
  }

  // --- Finance adjustments -----------------------------------------------------

  async function listFinanceAdjustments(
    filters: FinanceAdjustmentFilters = {},
  ): Promise<FinanceAdjustment[]> {
    const response = await client.get<ApiList<FinanceAdjustment>>('/finance-adjustments', {
      params: {
        adjustment_type: filters.adjustment_type || undefined,
        status: filters.status || undefined,
        agent_id: idOrUndefined(filters.agent_id),
      },
    })
    return response.data.data ?? []
  }

  async function getFinanceAdjustment(id: FinanceId): Promise<FinanceAdjustment> {
    const response = await client.get<ApiItem<FinanceAdjustment>>(`/finance-adjustments/${id}`)
    return response.data.data
  }

  async function createFinanceAdjustment(
    payload: FinanceAdjustmentPayload,
  ): Promise<FinanceAdjustment> {
    const response = await client.post<ApiItem<FinanceAdjustment>>('/finance-adjustments', payload)
    return response.data.data
  }

  async function postFinanceAdjustment(id: FinanceId): Promise<void> {
    await client.post(`/finance-adjustments/${id}/post`, {})
  }

  async function reverseFinanceAdjustment(id: FinanceId): Promise<void> {
    await client.post(`/finance-adjustments/${id}/reverse`, {})
  }

  // --- Reports -----------------------------------------------------------------

  async function payablesAging(
    filters: PayablesAgingFilters,
  ): Promise<ReportResult<PayablesAgingRow, PayablesAgingSummary>> {
    const response = await client.get<ApiReport<PayablesAgingRow, PayablesAgingSummary>>(
      '/reports/finance/payables-aging',
      {
        params: {
          as_of_date: filters.as_of_date,
          supplier_id: idOrUndefined(filters.supplier_id),
          currency_code: upper(filters.currency_code),
          status: filters.status || undefined,
          page: filters.page,
          per_page: filters.per_page,
        },
      },
    )
    return asReport(response.data)
  }

  async function cashFlowSummary(
    filters: DateRangeFilters,
  ): Promise<ReportResult<CashFlowSummaryRow, CashFlowSummaryTotals>> {
    const response = await client.get<ApiReport<CashFlowSummaryRow, CashFlowSummaryTotals>>(
      '/reports/finance/cash-flow-summary',
      {
        params: {
          date_from: filters.date_from,
          date_to: filters.date_to,
          currency_code: upper(filters.currency_code),
          group_by: filters.group_by,
        },
      },
    )
    return asReport(response.data)
  }

  async function revenueVsCollections(
    filters: DateRangeFilters,
  ): Promise<ReportResult<RevenueVsCollectionsRow, RevenueVsCollectionsTotals>> {
    const response = await client.get<
      ApiReport<RevenueVsCollectionsRow, RevenueVsCollectionsTotals>
    >('/reports/finance/revenue-vs-collections', {
      params: {
        date_from: filters.date_from,
        date_to: filters.date_to,
        currency_code: upper(filters.currency_code),
        group_by: filters.group_by,
      },
    })
    return asReport(response.data)
  }

  async function currencyExposure(
    asOfDate: string,
  ): Promise<ReportResult<CurrencyExposureRow, CurrencyExposureTotals>> {
    const response = await client.get<ApiReport<CurrencyExposureRow, CurrencyExposureTotals>>(
      '/reports/finance/currency-exposure',
      { params: { as_of_date: asOfDate } },
    )
    return asReport(response.data)
  }

  async function reconciliation(
    asOfDate: string,
  ): Promise<ReportResult<FinanceReconciliationRow, FinanceReconciliationTotals>> {
    const response = await client.get<
      ApiReport<FinanceReconciliationRow, FinanceReconciliationTotals>
    >('/reports/finance/reconciliation', { params: { as_of_date: asOfDate } })
    return asReport(response.data)
  }

  // --- Costing -----------------------------------------------------------------

  async function listCostings(
    filters: CostingFilters = {},
  ): Promise<PagedResult<CostingSnapshot>> {
    const response = await client.get<ApiList<CostingSnapshot>>('/costings', {
      params: {
        product_id: idOrUndefined(filters.product_id),
        production_type: filters.production_type || undefined,
        page: filters.page ?? 1,
        per_page: filters.per_page ?? 20,
      },
    })
    return asPaged(response.data.data ?? [], response.data.meta)
  }

  /**
   * Products for the costing picker. The endpoint is searchable and paginated,
   * so the picker pages against the server instead of the whole catalogue.
   */
  async function searchProducts(
    filters: {
      search?: string
      is_active?: FinanceFlag | ''
      page?: number
      per_page?: number
    } = {},
  ): Promise<PagedResult<ProductOption>> {
    const perPage = filters.per_page ?? 20
    const response = await client.get<ApiList<ProductOption>>('/products', {
      params: {
        search: filters.search || undefined,
        is_active: filters.is_active === '' ? undefined : filters.is_active,
        page: filters.page ?? 1,
        per_page: perPage,
      },
    })
    return asPaged(response.data.data ?? [], response.data.meta)
  }

  // --- Pricelists (read-only; used for margin analysis) ------------------------

  async function listPricelists(): Promise<PricelistOption[]> {
    const response = await client.get<ApiList<PricelistOption>>('/pricelists', {
      params: { page: 1, per_page: 100 },
    })
    return response.data.data ?? []
  }

  async function getPricelist(id: FinanceId): Promise<PricelistDetail> {
    const response = await client.get<ApiItem<PricelistDetail>>(`/pricelists/${id}`)
    return response.data.data
  }

  /**
   * Billable labour cost per employee (`GET /timesheets/report`). The endpoint
   * returns a plain array — it paginates neither by page nor by meta.
   */
  async function timesheetReport(
    filters: TimesheetReportFilters = {},
  ): Promise<TimesheetReportRow[]> {
    const response = await client.get<ApiItem<TimesheetReportRow[]>>('/timesheets/report', {
      params: {
        employee_id: idOrUndefined(filters.employee_id),
        department_agent_id: idOrUndefined(filters.department_agent_id),
        entry_type: filters.entry_type || undefined,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
        search: filters.search || undefined,
      },
    })
    return response.data.data ?? []
  }

  // --- Payroll (/payroll/*) ------------------------------------------------------

  /** Production staff that payroll items can be attached to (`GET /employees`). */
  async function listEmployeeOptions(): Promise<EmployeeOption[]> {
    const response = await client.get<ApiList<EmployeeOption>>('/employees', {
      params: { employee_type: 'production', is_active: 1, page: 1, per_page: 100 },
    })
    return response.data.data ?? []
  }

  async function listPayrollPeriods(
    filters: PayrollPeriodFilters = {},
  ): Promise<PagedResult<PayrollPeriod>> {
    const perPage = filters.per_page ?? 20
    const response = await client.get<ApiList<PayrollPeriod>>('/payroll/periods', {
      params: {
        status: filters.status || undefined,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
        search: filters.search || undefined,
        page: filters.page ?? 1,
        per_page: perPage,
      },
    })
    return {
      items: response.data.data ?? [],
      meta: parseMeta(response.data.meta, filters.page ?? 1, perPage),
    }
  }

  async function getPayrollPeriod(id: FinanceId): Promise<PayrollPeriod> {
    const response = await client.get<ApiItem<PayrollPeriod>>(`/payroll/periods/${id}`)
    return response.data.data
  }

  async function createPayrollPeriod(payload: PayrollPeriodPayload): Promise<PayrollPeriod> {
    const response = await client.post<ApiItem<PayrollPeriod>>('/payroll/periods', payload)
    return response.data.data
  }

  /** Only `open` periods can be updated. */
  async function updatePayrollPeriod(
    id: FinanceId,
    payload: PayrollPeriodPayload,
  ): Promise<PayrollPeriod> {
    const response = await client.put<ApiItem<PayrollPeriod>>(`/payroll/periods/${id}`, payload)
    return response.data.data
  }

  async function lockPayrollPeriod(id: FinanceId): Promise<void> {
    await client.post(`/payroll/periods/${id}/lock`)
  }

  async function closePayrollPeriod(id: FinanceId): Promise<void> {
    await client.post(`/payroll/periods/${id}/close`)
  }

  async function listLaborAccruals(
    filters: LaborAccrualFilters = {},
  ): Promise<PagedResult<LaborAccrual>> {
    const perPage = filters.per_page ?? 20
    const response = await client.get<ApiList<LaborAccrual>>('/payroll/accruals', {
      params: {
        payroll_period_id: idOrUndefined(filters.payroll_period_id),
        employee_id: idOrUndefined(filters.employee_id),
        production_order_id: idOrUndefined(filters.production_order_id),
        status: filters.status || undefined,
        stage_code: filters.stage_code || undefined,
        currency_code: upper(filters.currency_code),
        source_type: filters.source_type || undefined,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
        page: filters.page ?? 1,
        per_page: perPage,
      },
    })
    return {
      items: response.data.data ?? [],
      meta: parseMeta(response.data.meta, filters.page ?? 1, perPage),
    }
  }

  /** Only `provisional` accruals can be approved. */
  async function approveLaborAccrual(id: FinanceId): Promise<void> {
    await client.post(`/payroll/accruals/${id}/approve`)
  }

  async function rejectLaborAccrual(id: FinanceId, rejectionReason: string): Promise<void> {
    await client.post(`/payroll/accruals/${id}/reject`, {
      rejection_reason: rejectionReason,
    })
  }

  /** Only `approved` or `exported` accruals can be reversed. */
  async function reverseLaborAccrual(id: FinanceId, reversalReason: string): Promise<void> {
    await client.post(`/payroll/accruals/${id}/reverse`, {
      reversal_reason: reversalReason,
    })
  }

  async function listPayrollAdjustments(
    filters: PayrollAdjustmentFilters = {},
  ): Promise<PagedResult<PayrollAdjustment>> {
    const perPage = filters.per_page ?? 20
    const response = await client.get<ApiList<PayrollAdjustment>>('/payroll/adjustments', {
      params: {
        employee_id: idOrUndefined(filters.employee_id),
        payroll_period_id: idOrUndefined(filters.payroll_period_id),
        status: filters.status || undefined,
        type: filters.type || undefined,
        currency_code: upper(filters.currency_code),
        page: filters.page ?? 1,
        per_page: perPage,
      },
    })
    return {
      items: response.data.data ?? [],
      meta: parseMeta(response.data.meta, filters.page ?? 1, perPage),
    }
  }

  async function getPayrollAdjustment(id: FinanceId): Promise<PayrollAdjustment> {
    const response = await client.get<ApiItem<PayrollAdjustment>>(`/payroll/adjustments/${id}`)
    return response.data.data
  }

  async function createPayrollAdjustment(
    payload: PayrollAdjustmentPayload,
  ): Promise<PayrollAdjustment> {
    const response = await client.post<ApiItem<PayrollAdjustment>>(
      '/payroll/adjustments',
      payload,
    )
    return response.data.data
  }

  /** Only `draft` adjustments can be updated. */
  async function updatePayrollAdjustment(
    id: FinanceId,
    payload: Partial<PayrollAdjustmentPayload>,
  ): Promise<PayrollAdjustment> {
    const response = await client.put<ApiItem<PayrollAdjustment>>(
      `/payroll/adjustments/${id}`,
      payload,
    )
    return response.data.data
  }

  /** Only `draft` adjustments can be approved. */
  async function approvePayrollAdjustment(id: FinanceId): Promise<void> {
    await client.post(`/payroll/adjustments/${id}/approve`)
  }

  async function rejectPayrollAdjustment(id: FinanceId, rejectionReason: string): Promise<void> {
    await client.post(`/payroll/adjustments/${id}/reject`, {
      rejection_reason: rejectionReason,
    })
  }

  /** Only `approved` or `used` adjustments can be reversed. */
  async function reversePayrollAdjustment(id: FinanceId, reversalReason: string): Promise<void> {
    await client.post(`/payroll/adjustments/${id}/reverse`, {
      reversal_reason: reversalReason,
    })
  }

  async function listPayrollRuns(
    filters: PayrollRunFilters = {},
  ): Promise<PagedResult<PayrollRunSummary>> {
    const perPage = filters.per_page ?? 20
    const response = await client.get<ApiList<PayrollRunSummary>>('/payroll/runs', {
      params: {
        payroll_period_id: idOrUndefined(filters.payroll_period_id),
        status: filters.status || undefined,
        currency_code: upper(filters.currency_code),
        search: filters.search || undefined,
        page: filters.page ?? 1,
        per_page: perPage,
      },
    })
    return {
      items: response.data.data ?? [],
      meta: parseMeta(response.data.meta, filters.page ?? 1, perPage),
    }
  }

  async function getPayrollRun(id: FinanceId): Promise<PayrollRunDetail> {
    const response = await client.get<ApiItem<PayrollRunDetail>>(`/payroll/runs/${id}`)
    return response.data.data
  }

  /**
   * Generates (or returns) the draft run for one period and one currency.
   * The endpoint does not document a body, so the payload may be empty.
   */
  async function generatePayrollRun(payload: PayrollRunPayload): Promise<PayrollRunDetail | null> {
    const response = await client.post<ApiItem<PayrollRunDetail>>('/payroll/runs', payload)
    return response.data?.data ?? null
  }

  /** Releases the previous accruals/adjustments and regenerates draft lines. */
  async function recalculatePayrollRun(id: FinanceId): Promise<void> {
    await client.post(`/payroll/runs/${id}/recalculate`)
  }

  async function approvePayrollRun(id: FinanceId): Promise<void> {
    await client.post(`/payroll/runs/${id}/approve`)
  }

  /** Creates one posting + treasury outflow per employee line with positive net pay. */
  async function postPayrollRun(id: FinanceId, financialAccountId: FinanceId): Promise<void> {
    await client.post(`/payroll/runs/${id}/post`, {
      financial_account_id: financialAccountId,
    })
  }

  async function listPayrollPostings(
    filters: PayrollPostingFilters = {},
  ): Promise<PagedResult<PayrollPosting>> {
    const perPage = filters.per_page ?? 20
    const response = await client.get<ApiList<PayrollPosting>>('/payroll/postings', {
      params: {
        payroll_run_id: idOrUndefined(filters.payroll_run_id),
        employee_id: idOrUndefined(filters.employee_id),
        financial_account_id: idOrUndefined(filters.financial_account_id),
        status: filters.status || undefined,
        currency_code: upper(filters.currency_code),
        page: filters.page ?? 1,
        per_page: perPage,
      },
    })
    return {
      items: response.data.data ?? [],
      meta: parseMeta(response.data.meta, filters.page ?? 1, perPage),
    }
  }

  async function reversePayrollPosting(id: FinanceId, reversalReason: string): Promise<void> {
    await client.post(`/payroll/postings/${id}/reverse`, {
      reversal_reason: reversalReason,
    })
  }

  // --- Payroll reports (/reports/payroll/*) -------------------------------------

  /** Totals for the payroll dashboard: runs, accruals, postings and headcount. */
  async function payrollSummary(
    filters: PayrollReportFilters = {},
  ): Promise<PayrollSummaryReport | null> {
    const response = await client.get<ApiItem<PayrollSummaryReport>>('/reports/payroll/summary', {
      params: reportParams(filters),
    })
    return response.data?.data ?? null
  }

  /** One employee's earnings, deductions and postings. `employee_id` is required. */
  async function payrollEmployeeStatement(
    filters: PayrollReportFilters,
  ): Promise<PayrollEmployeeStatement | null> {
    const response = await client.get<ApiItem<PayrollEmployeeStatement>>(
      '/reports/payroll/employee-statement',
      { params: reportParams(filters) },
    )
    return response.data?.data ?? null
  }

  /** Accrual totals compared against payroll earning lines. */
  async function payrollAccrualReconciliation(
    filters: PayrollReportFilters = {},
  ): Promise<
    ReportResult<PayrollAccrualReconciliationRow, PayrollAccrualReconciliationSummary>
  > {
    const response = await client.get<
      ApiReport<PayrollAccrualReconciliationRow, PayrollAccrualReconciliationSummary>
    >('/reports/payroll/accrual-reconciliation', { params: reportParams(filters) })
    return asReport(response.data)
  }

  /** Run net totals compared against active payroll postings. */
  async function payrollPostingReconciliation(
    filters: PayrollReportFilters = {},
  ): Promise<
    ReportResult<PayrollPostingReconciliationRow, PayrollPostingReconciliationSummary>
  > {
    const response = await client.get<
      ApiReport<PayrollPostingReconciliationRow, PayrollPostingReconciliationSummary>
    >('/reports/payroll/posting-reconciliation', { params: reportParams(filters) })
    return asReport(response.data)
  }

  return {
    listCurrencies,
    listAllAgentsByRole,
    listCounterpartyOptions,
    listWorkplaceOptions,
    listCounterpartyBalances,
    listInvoices,
    getInvoice,
    listPayments,
    getPayment,
    createPayment,
    updatePayment,
    deletePayment,
    reversePayment,
    listAllocationHistory,
    listSupplierInvoices,
    getSupplierInvoice,
    createSupplierInvoice,
    updateSupplierInvoice,
    deleteSupplierInvoice,
    voidSupplierInvoice,
    listPurchaseOrders,
    listFinancialAccounts,
    getFinancialAccount,
    createFinancialAccount,
    updateFinancialAccount,
    listAccountTransactions,
    listFinancialTransfers,
    getFinancialTransfer,
    createFinancialTransfer,
    reverseFinancialTransfer,
    listFinanceAdjustments,
    getFinanceAdjustment,
    createFinanceAdjustment,
    postFinanceAdjustment,
    reverseFinanceAdjustment,
    payablesAging,
    cashFlowSummary,
    revenueVsCollections,
    currencyExposure,
    reconciliation,
    listCostings,
    searchProducts,
    listPricelists,
    getPricelist,
    timesheetReport,
    listEmployeeOptions,
    listPayrollPeriods,
    getPayrollPeriod,
    createPayrollPeriod,
    updatePayrollPeriod,
    lockPayrollPeriod,
    closePayrollPeriod,
    listLaborAccruals,
    approveLaborAccrual,
    rejectLaborAccrual,
    reverseLaborAccrual,
    listPayrollAdjustments,
    getPayrollAdjustment,
    createPayrollAdjustment,
    updatePayrollAdjustment,
    approvePayrollAdjustment,
    rejectPayrollAdjustment,
    reversePayrollAdjustment,
    listPayrollRuns,
    getPayrollRun,
    generatePayrollRun,
    recalculatePayrollRun,
    approvePayrollRun,
    postPayrollRun,
    listPayrollPostings,
    reversePayrollPosting,
    payrollSummary,
    payrollEmployeeStatement,
    payrollAccrualReconciliation,
    payrollPostingReconciliation,
  }
}
